import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Video, MessageCircle, ShoppingBag, Calendar, Users, Eye, 
  Star, MapPin, Clock, Send, Heart, Play, ChevronRight,
  Radio, Gavel, DollarSign, ThumbsUp, Truck, MessageSquare,
  ChevronUp, ChevronDown, Volume2, VolumeX, Gift, Trophy, ExternalLink,
  Search, X, ChevronLeft, Share2, Bookmark, MoreHorizontal,
  Smartphone, Tv, Zap, Tag, ArrowRight, Plus, Globe, Mail, User
} from "lucide-react";
import { StreamBroadcaster, StreamViewer, CreateStreamDialog } from "@/components/BidZuuLive";
import LiveWatch from "@/components/MobileLiveWatch";
import MobileBroadcaster from "@/components/MobileBroadcaster";
import api from "@/utils/api";

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// BidZuu Categories for navigation
const BIDZUU_CATEGORIES = [
  { name: "All", icon: "🏠" },
  { name: "Fashion", icon: "👗" },
  { name: "Electronics", icon: "📱" },
  { name: "Collectibles", icon: "🎭" },
  { name: "Toys & Games", icon: "🎮" },
  { name: "Home & Garden", icon: "🏡" },
  { name: "Sports", icon: "⚽" },
  { name: "Motors", icon: "🚗" },
  { name: "Trading Cards", icon: "🃏" },
  { name: "Coins & Bullion", icon: "🪙" },
];

// Full Page Header Component - Two Tier Style (like eBay/Poshmark)
const LivePageHeader = ({ user, searchQuery, setSearchQuery, navigate }) => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
      {/* First Row - Account Actions */}
      <div className="border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-10 text-sm">
            {/* Left Side */}
            <div className="flex items-center gap-4">
              {user ? (
                <span className="text-gray-700">
                  Hi! <Link to="/account" className="text-emerald-600 hover:underline font-medium">{user.first_name || user.name || 'User'}</Link>
                </span>
              ) : (
                <span className="text-gray-700">
                  Hi! <Link to="/auth" className="text-emerald-600 hover:underline">Sign in</Link> or <Link to="/auth" className="text-emerald-600 hover:underline">register</Link>
                </span>
              )}
              <Link to="/browse" className="text-gray-700 hover:text-emerald-600">Daily Deals</Link>
            </div>
            
            {/* Right Side */}
            <div className="flex items-center gap-4">
              <Link to="/sell" className="text-gray-700 hover:text-emerald-600">Sell</Link>
              <Link to="/dashboard?tab=watchlist" className="text-gray-700 hover:text-emerald-600 flex items-center gap-1">
                <Heart className="h-4 w-4" /> Watchlist
              </Link>
              <Link to="/account" className="text-gray-700 hover:text-emerald-600">My BidZuu</Link>
              <Link to="/messages" className="text-gray-700 hover:text-emerald-600">
                <Mail className="h-4 w-4" />
              </Link>
              <Link to="/dashboard?tab=watchlist" className="text-gray-700 hover:text-emerald-600">
                <Heart className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      {/* Second Row - Logo, Search, and Navigation Buttons */}
      <div className="border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 h-14">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/bidzuu_logo_original.png" 
                alt="BidZuu" 
                className="h-10 w-auto"
              />
            </Link>
            
            {/* Search Bar - Full Size */}
            <div className="flex-1 max-w-xl">
              <div className="flex">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search for anything"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 h-10 border-gray-300 rounded-l-full rounded-r-none focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid="live-search-input"
                  />
                </div>
                <Button className="h-10 px-6 bg-emerald-500 hover:bg-emerald-600 rounded-l-none rounded-r-full">
                  Search
                </Button>
              </div>
            </div>
            
            {/* Right Side - Just the heart/favorites button */}
            <div className="flex items-center gap-4">
              <button className="p-2 hover:bg-gray-100 rounded-full">
                <Heart className="h-6 w-6 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Featured Live Stream Card - Poshmark Style
const LiveStreamCard = ({ stream, onClick }) => {
  const [liked, setLiked] = useState(false);
  
  return (
    <div 
      onClick={onClick}
      className="group relative bg-slate-800 rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-emerald-500 transition-all"
      data-testid={`stream-card-${stream.stream_id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-[4/5]">
        <img 
          src={stream.thumbnail || "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400"} 
          alt={stream.title}
          className="w-full h-full object-cover"
        />
        
        {/* Live Badge with Viewer Count */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <Badge className="bg-red-500 text-white px-2 py-1 text-xs font-bold animate-pulse">
            LIVE
          </Badge>
          <Badge className="bg-black/60 text-white px-2 py-1 text-xs">
            <Eye className="h-3 w-3 mr-1 inline" />
            {stream.viewer_count || Math.floor(Math.random() * 500) + 50}
          </Badge>
        </div>
        
        {/* Bookmark Button */}
        <button 
          onClick={(e) => { e.stopPropagation(); setLiked(!liked); }}
          className="absolute top-3 right-3 w-8 h-8 bg-black/50 rounded-lg flex items-center justify-center hover:bg-black/70 transition"
        >
          <Bookmark className={`h-4 w-4 ${liked ? 'fill-white text-white' : 'text-white'}`} />
        </button>
        
        {/* Gradient Overlay at Bottom */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/90 to-transparent" />
        
        {/* Host Info at Bottom */}
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2 mb-2">
            <Avatar className="w-8 h-8 border-2 border-emerald-500">
              <AvatarImage src={stream.host_avatar} />
              <AvatarFallback className="bg-emerald-500 text-white text-xs">
                {stream.host_name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">{stream.host_name}</p>
              <p className="text-emerald-400 text-xs">{stream.category || "Shopping"}</p>
            </div>
          </div>
          <p className="text-white text-sm line-clamp-2">{stream.title}</p>
        </div>
      </div>
    </div>
  );
};

// TikTok Style Video Card
const TikTokVideoCard = ({ video }) => {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(video.likes || Math.floor(Math.random() * 50000) + 1000);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  return (
    <div className="bg-slate-800 rounded-xl overflow-hidden">
      <div className="aspect-[9/16] max-h-[400px] bg-black">
        <iframe
          src={video.embed_url || `https://www.youtube.com/embed/${video.video_id}?rel=0`}
          className="w-full h-full"
          allowFullScreen
          allow="encrypted-media"
          style={{ border: 'none' }}
        />
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
            <span className="text-white text-xs font-bold">
              {video.username?.charAt(1)?.toUpperCase() || 'B'}
            </span>
          </div>
          <span className="text-white font-medium text-sm">{video.username || '@bidzuu'}</span>
        </div>
        <p className="text-slate-400 text-xs line-clamp-2">{video.description || video.title}</p>
        <div className="flex items-center justify-between mt-2">
          <button 
            onClick={() => { setLikes(prev => liked ? prev - 1 : prev + 1); setLiked(!liked); }}
            className={`flex items-center gap-1 ${liked ? 'text-red-500' : 'text-slate-400'}`}
          >
            <Heart className={`h-4 w-4 ${liked ? 'fill-red-500' : ''}`} />
            <span className="text-xs">{formatNumber(likes)}</span>
          </button>
          <a 
            href={video.watch_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 text-xs hover:underline"
          >
            Watch <ExternalLink className="h-3 w-3 inline" />
          </a>
        </div>
      </div>
    </div>
  );
};

// Sidebar Category Item
const CategoryItem = ({ category, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition text-left ${
      isActive 
        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
        : 'hover:bg-slate-800 text-slate-300'
    }`}
  >
    <span className="text-xl">{category.icon}</span>
    <span className="font-medium">{category.name}</span>
  </button>
);

// My Likes Section
const MyLikesSection = ({ likedStreams = [] }) => (
  <div className="bg-slate-800/50 rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-white font-semibold">My Likes</h3>
      <a href="#" className="text-emerald-400 text-sm hover:underline">ALL LIKES</a>
    </div>
    {likedStreams.length > 0 ? (
      <div className="space-y-2">
        {likedStreams.slice(0, 3).map((stream, idx) => (
          <div key={idx} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer">
            <img src={stream.thumbnail} alt="" className="w-10 h-10 rounded object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate">{stream.title}</p>
              <p className="text-slate-400 text-xs">{stream.host_name}</p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-4">
        <Heart className="h-8 w-8 text-slate-600 mx-auto mb-2" />
        <p className="text-slate-400 text-sm">"Like" streams to save them for later.</p>
        <Button variant="link" className="text-emerald-400 mt-2">Browse Now!</Button>
      </div>
    )}
  </div>
);

// Recent Views Section
const RecentViewsSection = ({ recentViews = [] }) => (
  <div className="bg-slate-800/50 rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-white font-semibold">Recent Views</h3>
      <a href="#" className="text-emerald-400 text-sm hover:underline">VIEW ALL</a>
    </div>
    {recentViews.length > 0 ? (
      <div className="flex gap-2 overflow-x-auto pb-2">
        {recentViews.slice(0, 5).map((stream, idx) => (
          <div key={idx} className="flex-shrink-0 w-16">
            <img src={stream.thumbnail} alt="" className="w-16 h-16 rounded-lg object-cover" />
          </div>
        ))}
      </div>
    ) : (
      <p className="text-slate-400 text-sm text-center py-2">No recent views yet</p>
    )}
  </div>
);

// Featured Shows Sidebar Section
const FeaturedShowsSection = ({ streams, onStreamClick }) => (
  <div className="bg-slate-800/50 rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-white font-semibold flex items-center gap-2">
        <Radio className="h-4 w-4 text-red-500" />
        BidZuu Shows
      </h3>
      <a href="#" className="text-emerald-400 text-sm hover:underline">ALL SHOWS</a>
    </div>
    <div className="space-y-3">
      {streams.slice(0, 4).map((stream) => (
        <div 
          key={stream.stream_id}
          onClick={() => onStreamClick(stream)}
          className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer"
        >
          <div className="relative">
            <img 
              src={stream.thumbnail} 
              alt="" 
              className="w-14 h-14 rounded-lg object-cover"
            />
            <div className="absolute -top-1 -left-1">
              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0.5">
                LIVE
              </Badge>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{stream.title}</p>
            <p className="text-slate-400 text-xs flex items-center gap-1">
              <Eye className="h-3 w-3" /> {stream.viewer_count || Math.floor(Math.random() * 200) + 20}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Today's Trends Section
const TodaysTrends = () => {
  const trends = [
    { name: "Vintage Fashion", image: "https://images.unsplash.com/photo-1558171813-4c088753af8f?w=200" },
    { name: "Rare Collectibles", image: "https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=200" },
    { name: "Tech Deals", image: "https://images.unsplash.com/photo-1468495244123-6c6c332eeece?w=200" },
  ];
  
  return (
    <div className="bg-slate-800/50 rounded-xl p-4">
      <h3 className="text-white font-semibold mb-3">Today's Trends</h3>
      <p className="text-slate-400 text-xs mb-3">
        Discover what's hot on BidZuu right now
      </p>
      <div className="grid grid-cols-3 gap-2">
        {trends.map((trend, idx) => (
          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group">
            <img src={trend.image} alt={trend.name} className="w-full h-full object-cover group-hover:scale-110 transition" />
            <div className="absolute inset-0 bg-black/50 flex items-end p-2">
              <p className="text-white text-xs font-medium">{trend.name}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main BidZuu Live Page - Full Page Poshmark Style
export const BidZuuLivePage = ({ user }) => {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [tiktokVideos, setTiktokVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live'); // 'live' or 'tiktok'
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [userListings, setUserListings] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [likedStreams, setLikedStreams] = useState([]);
  const [recentViews, setRecentViews] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch live streams
        const streamsRes = await api.get('/live/streams');
        setStreams(streamsRes.data.streams || []);
        
        // Fetch TikTok/entertainment videos
        try {
          const videosRes = await api.get('/entertainment/feed');
          setTiktokVideos(videosRes.data.videos || []);
        } catch (error) {
          console.error('Error fetching videos:', error);
        }
        
        // Fetch user listings if logged in
        if (user) {
          const listingsRes = await api.get('/listings/my');
          setUserListings(listingsRes.data.listings || []);
        }
      } catch (error) {
        console.error('Error fetching streams:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  const handleStreamClick = (stream) => {
    // Add to recent views
    setRecentViews(prev => [stream, ...prev.filter(s => s.stream_id !== stream.stream_id)].slice(0, 10));
    navigate(`/live/${stream.stream_id}`);
  };

  const handleStreamCreated = (streamId) => {
    setShowCreateDialog(false);
    navigate(`/live/${streamId}`);
  };

  const filteredStreams = selectedCategory === 'All' 
    ? streams 
    : streams.filter(s => s.category?.toLowerCase() === selectedCategory.toLowerCase());

  const searchedStreams = searchQuery 
    ? filteredStreams.filter(s => 
        s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.host_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredStreams;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="bidzuu-live-page">
      {/* Full Page Header - Poshmark Style */}
      <LivePageHeader 
        user={user} 
        searchQuery={searchQuery} 
        setSearchQuery={setSearchQuery}
        navigate={navigate}
      />

      {/* Main Content - With padding for fixed header */}
      <div className="pt-[124px]">
        {/* Live Page Title Bar */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 py-6">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <Radio className="h-8 w-8 text-red-500 animate-pulse" />
                  BidZuu Live
                </h1>
                <p className="text-slate-400 mt-1">Watch live auctions and shopping streams</p>
              </div>
              <div className="flex items-center gap-3">
                {user && (
                  <Button 
                    onClick={() => navigate('/my-shows')}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10"
                    data-testid="my-shows-btn"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    My Shows
                  </Button>
                )}
                <Button 
                  onClick={() => user ? setShowCreateDialog(true) : navigate('/auth')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
                  data-testid="go-live-btn"
                >
                  <Video className="h-5 w-5 mr-2" />
                  Go Live
                </Button>
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setActiveTab('live')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition ${
                  activeTab === 'live' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
                data-testid="tab-live-streams"
              >
                <Radio className="h-4 w-4" />
                Live Streams
              </button>
              <button
                onClick={() => setActiveTab('tiktok')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition ${
                  activeTab === 'tiktok' 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
                data-testid="tab-tiktok"
              >
                <Play className="h-4 w-4" />
                TikTok Videos
              </button>
            </div>
          </div>
        </div>

        {/* 3-Column Layout */}
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Left Sidebar - Categories & Personal */}
            <div className="lg:col-span-2 space-y-4">
              {/* Categories */}
              <div className="bg-slate-800/30 rounded-xl p-3">
                <h3 className="text-white font-semibold px-4 py-2 flex items-center justify-between">
                  Categories
                  <span className="text-emerald-400 text-xs cursor-pointer hover:underline">ALL</span>
                </h3>
                <div className="space-y-1">
                  {BIDZUU_CATEGORIES.map((cat) => (
                    <CategoryItem
                      key={cat.name}
                      category={cat}
                      isActive={selectedCategory === cat.name}
                      onClick={() => setSelectedCategory(cat.name)}
                    />
                  ))}
                </div>
              </div>
              
              {/* My Likes */}
              <MyLikesSection likedStreams={likedStreams} />
              
              {/* Recent Views */}
              <RecentViewsSection recentViews={recentViews} />
            </div>

            {/* Center - Main Content */}
            <div className="lg:col-span-7">
              {activeTab === 'live' ? (
                <>
                  {/* Live Now Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                        <h2 className="text-white font-bold text-lg">Live Now</h2>
                      </div>
                      <Badge variant="outline" className="border-emerald-500 text-emerald-400">
                        {searchedStreams.length} streams
                      </Badge>
                    </div>
                    <button className="text-slate-400 hover:text-white text-sm flex items-center gap-1">
                      Show less <ChevronUp className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Streams Grid */}
                  {searchedStreams.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                      {searchedStreams.map((stream) => (
                        <LiveStreamCard
                          key={stream.stream_id}
                          stream={stream}
                          onClick={() => handleStreamClick(stream)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-slate-800/30 rounded-xl">
                      <Video className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                      <h3 className="text-white font-semibold mb-2">No Live Streams</h3>
                      <p className="text-slate-400 text-sm mb-4">
                        {searchQuery ? `No streams matching "${searchQuery}"` : 'Be the first to go live!'}
                      </p>
                      <Button 
                        onClick={() => user ? setShowCreateDialog(true) : navigate('/auth')}
                        className="bg-emerald-500 hover:bg-emerald-600"
                      >
                        Start Streaming
                      </Button>
                    </div>
                  )}
                  
                  {/* How It Works Section */}
                  <div className="mt-8 bg-gradient-to-r from-emerald-900/30 to-slate-800/30 rounded-xl p-6">
                    <h3 className="text-white font-bold text-lg mb-4">How BidZuu Live Works</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Video className="h-6 w-6 text-emerald-400" />
                        </div>
                        <h4 className="text-white font-semibold mb-1">Go Live</h4>
                        <p className="text-slate-400 text-sm">Start streaming to showcase your products</p>
                      </div>
                      <div className="text-center">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Users className="h-6 w-6 text-emerald-400" />
                        </div>
                        <h4 className="text-white font-semibold mb-1">Connect</h4>
                        <p className="text-slate-400 text-sm">Engage with viewers in real-time chat</p>
                      </div>
                      <div className="text-center">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Gavel className="h-6 w-6 text-emerald-400" />
                        </div>
                        <h4 className="text-white font-semibold mb-1">Sell</h4>
                        <p className="text-slate-400 text-sm">Viewers can bid or buy instantly</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* TikTok Videos Tab */
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-white font-bold text-lg flex items-center gap-2">
                      <Play className="h-5 w-5 text-pink-500" />
                      Trending Videos
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                    {tiktokVideos.map((video, idx) => (
                      <TikTokVideoCard key={video.video_id || idx} video={video} />
                    ))}
                  </div>
                  
                  {tiktokVideos.length === 0 && (
                    <div className="text-center py-12 bg-slate-800/30 rounded-xl">
                      <Play className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                      <h3 className="text-white font-semibold mb-2">No Videos Available</h3>
                      <p className="text-slate-400 text-sm">Check back later for trending content</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right Sidebar - Featured Shows & Trends */}
            <div className="lg:col-span-3 space-y-4">
              {/* Featured Shows */}
              <FeaturedShowsSection 
                streams={streams} 
                onStreamClick={handleStreamClick} 
              />
              
              {/* Today's Trends */}
              <TodaysTrends />
              
              {/* Download App CTA */}
              <div className="bg-gradient-to-br from-emerald-900/50 to-slate-800 rounded-xl p-4 text-center">
                <Smartphone className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                <h3 className="text-white font-semibold mb-1">Get the App</h3>
                <p className="text-slate-400 text-xs mb-3">
                  Watch streams on the go
                </p>
                <a 
                  href="https://apps.apple.com/us/app/bidzuu/id6752724318"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <div className="bg-white rounded-lg px-4 py-2 text-slate-900 text-sm font-medium hover:bg-gray-100 transition-colors">
                    Download on App Store
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Stream Dialog */}
      <CreateStreamDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={handleStreamCreated}
        userListings={userListings}
      />
    </div>
  );
};

// ============================================
// STREAM WATCH PAGE - Poshmark Live Show Style
// ============================================

export const StreamWatchPage = ({ user }) => {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [stream, setStream] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [currentItem, setCurrentItem] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [streamLiked, setStreamLiked] = useState(false);
  const [streamLikes, setStreamLikes] = useState(0);
  const [searchListings, setSearchListings] = useState('');
  const chatEndRef = useRef(null);

  // Mock listings for the sidebar
  const [listings] = useState([
    { id: 1, title: "Vintage Designer Handbag", price: 45, size: "One Size", brand: "Coach", image: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=200" },
    { id: 2, title: "Limited Edition Sneakers", price: 89, size: "10", brand: "Nike", image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=200" },
    { id: 3, title: "Rare Comic Book Collection", price: 150, size: "N/A", brand: "Marvel", image: "https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=200" },
    { id: 4, title: "Antique Watch - 1950s", price: 320, size: "N/A", brand: "Omega", image: "https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=200" },
    { id: 5, title: "Gaming Console Bundle", price: 275, size: "N/A", brand: "Sony", image: "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=200" },
  ]);

  // Activity feed messages
  const [activityFeed] = useState([
    { type: 'join', user: 'fashionista_22', time: '2m ago' },
    { type: 'comment', user: 'collector_mike', message: 'Great deals tonight!', time: '1m ago' },
    { type: 'bid', user: 'shopper_jane', item: 'Vintage Handbag', amount: 50, time: '45s ago' },
    { type: 'comment', user: 'deal_hunter', message: 'Is shipping included?', time: '30s ago' },
    { type: 'like', user: 'newbie_buyer', time: '15s ago' },
  ]);

  // Send chat message
  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    
    const newMessage = {
      id: Date.now(),
      sender_name: user?.name || user?.first_name || 'Guest',
      message: chatInput.trim(),
      timestamp: new Date().toISOString()
    };
    
    setChatMessages(prev => [...prev, newMessage]);
    setChatInput('');
    
    // Scroll to bottom
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Like the stream
  const handleLikeStream = () => {
    if (streamLiked) {
      setStreamLikes(prev => prev - 1);
    } else {
      setStreamLikes(prev => prev + 1);
    }
    setStreamLiked(!streamLiked);
  };

  // Place bid
  const handlePlaceBid = () => {
    if (!currentItem || !bidAmount) return;
    
    // Add to chat as bid activity
    const bidMessage = {
      id: Date.now(),
      sender_name: user?.name || user?.first_name || 'Guest',
      message: `Placed a bid of $${bidAmount} on ${currentItem.title}`,
      type: 'bid',
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, bidMessage]);
    setBidAmount('');
  };

  useEffect(() => {
    const fetchStream = async () => {
      try {
        const res = await api.get(`/live/streams/${streamId}`);
        setStream(res.data);
        
        // Set first product as current item
        if (res.data.products?.length > 0) {
          setCurrentItem(res.data.products[0]);
        } else {
          setCurrentItem(listings[0]);
        }
        
        // Initialize stream likes
        setStreamLikes(res.data.likes || Math.floor(Math.random() * 500) + 100);
        
        // Get token to join
        const tokenRes = await api.post(`/live/streams/${streamId}/token`);
        setTokenData(tokenRes.data);
      } catch (err) {
        setError('Stream not found');
      } finally {
        setLoading(false);
      }
    };

    fetchStream();
  }, [streamId]);

  const handleStreamStart = async () => {
    try {
      await api.put(`/live/streams/${streamId}/start`);
      setStream(prev => ({ ...prev, status: 'live' }));
    } catch (err) {
      console.error('Error starting stream:', err);
    }
  };

  const handleStreamEnd = async () => {
    try {
      await api.put(`/live/streams/${streamId}/end`);
      setStream(prev => ({ ...prev, status: 'ended' }));
      navigate('/live');
    } catch (err) {
      console.error('Error ending stream:', err);
    }
  };

  const handlePurchase = (product) => {
    navigate(`/item/${product.listing_id || product.id}`);
  };

  const filteredListings = searchListings 
    ? listings.filter(l => l.title.toLowerCase().includes(searchListings.toLowerCase()))
    : listings;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !stream) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Card className="p-8 text-center bg-slate-800 border-slate-700 max-w-md">
          <Video className="h-16 w-16 mx-auto mb-4 text-slate-500" />
          <h2 className="text-xl font-semibold mb-2 text-white">Stream Not Found</h2>
          <p className="text-slate-400 mb-4">This stream may have ended or does not exist.</p>
          <Button onClick={() => navigate('/live')} className="bg-emerald-500 hover:bg-emerald-600">
            Back to Live
          </Button>
        </Card>
      </div>
    );
  }

  const isHost = tokenData?.is_host;

  // Mobile Host - Show MobileBroadcaster for hosts streaming from their phones
  if (isHost && isMobile) {
    return (
      <MobileBroadcaster
        stream={stream}
        products={stream?.products || listings}
        onEndStream={handleStreamEnd}
        user={user}
      />
    );
  }

  // Desktop Host - Show desktop broadcaster view
  if (isHost && !isMobile) {
    return (
      <div className="min-h-screen bg-slate-900" data-testid="desktop-broadcaster">
        {/* Header */}
        <div className="bg-slate-900/95 border-b border-slate-800 px-4 py-3 sticky top-0 z-50">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/my-shows')}
                className="text-slate-400 hover:text-white"
              >
                <ChevronLeft className="h-5 w-5 mr-1" /> My Shows
              </Button>
              <div>
                <h1 className="text-white font-bold">{stream?.title}</h1>
                <p className="text-slate-400 text-sm">Broadcaster Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-red-500 text-white animate-pulse">
                <Radio className="h-3 w-3 mr-1" /> HOSTING
              </Badge>
              <Badge variant="secondary" className="bg-slate-700 text-white">
                <Eye className="h-3 w-3 mr-1" /> {stream?.viewer_count || 0} viewers
              </Badge>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Left - Products Panel */}
            <div className="col-span-3 bg-slate-800 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-emerald-400" />
                Products ({(stream?.products || listings).length})
              </h3>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {(stream?.products || listings).map((product, idx) => (
                  <div 
                    key={product.id || idx}
                    className={`p-3 rounded-lg cursor-pointer transition ${
                      currentItem?.id === product.id 
                        ? 'bg-emerald-500/20 border border-emerald-500' 
                        : 'bg-slate-700/50 hover:bg-slate-700'
                    }`}
                    onClick={() => setCurrentItem(product)}
                  >
                    <div className="flex items-center gap-3">
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-12 h-12 rounded object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{product.title}</p>
                        <p className="text-emerald-400 font-bold">${product.price}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Center - Video & Controls */}
            <div className="col-span-6">
              <StreamBroadcaster
                streamId={streamId}
                channelName={tokenData?.channel_name}
                token={tokenData?.token}
                appId={tokenData?.app_id}
                uid={tokenData?.uid}
                onStreamStart={handleStreamStart}
                onStreamEnd={handleStreamEnd}
                products={stream?.products || listings}
              />
              
              {/* Current Item Highlight */}
              {currentItem && (
                <div className="mt-4 bg-slate-800 rounded-xl p-4">
                  <h4 className="text-slate-400 text-sm mb-2">Currently Featuring</h4>
                  <div className="flex items-center gap-4">
                    <img 
                      src={currentItem.image} 
                      alt={currentItem.title}
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-white font-semibold">{currentItem.title}</p>
                      <p className="text-emerald-400 text-xl font-bold">${currentItem.price}</p>
                    </div>
                    <Button className="bg-emerald-500 hover:bg-emerald-600">
                      <Gavel className="h-4 w-4 mr-2" /> Start Auction
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Right - Chat & Activity */}
            <div className="col-span-3 bg-slate-800 rounded-xl flex flex-col h-[600px]">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-emerald-400" />
                  Live Chat
                </h3>
              </div>
              
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg) => (
                  <div key={msg.id} className="flex items-start gap-2">
                    <Avatar className="w-7 h-7">
                      <AvatarFallback className="bg-emerald-500 text-white text-xs">
                        {msg.sender_name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-white text-sm">
                        <span className="font-semibold">{msg.sender_name}</span>{' '}
                        <span className="text-slate-300">{msg.message}</span>
                      </p>
                    </div>
                  </div>
                ))}
                {chatMessages.length === 0 && (
                  <div className="text-center text-slate-500 py-8">
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No messages yet</p>
                    <p className="text-xs">Chat will appear here when viewers join</p>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              
              {/* Chat Input */}
              <div className="p-4 border-t border-slate-700">
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
                    placeholder="Send a message..."
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                  <Button 
                    onClick={handleSendChat}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Viewer - Show the Poshmark-style live watch experience
  return <LiveWatch user={user} />;
};

export default BidZuuLivePage;
